import { PayloadAction } from '@reduxjs/toolkit';
import {
    companyQuestionAnswer,
    createCompany,
    fetchActiveChatQuestions,
    fetchChatMessages,
    fetchChatsList,
    fetchCompanyCategories,
    fetchCompanyQuestionsHistory,
    fetchCreateJobCompanysList,
    fetchCreateJobQuestionsCategories,
} from '../../apis/tools';
import { call, put, select } from 'redux-saga/effects';
import {
    IClientChat,
    IClientChatHistory,
    IClientQuestionCategory,
    ICompanyQuestion,
    IServerChat,
    IServerChatHistoryMessage,
    IUser,
    MessageTypes,
    SnackbarUtils,
    TempMessageAndChatIds,
} from '../../../common';
import { toolsActions, toolsSelectors } from '../redux';
import {
    IFetchActiveChatHistoryAction,
    IFetchChatsListAction,
    IFetchCompanyCategoriesAction,
    IFetchCompanyQuestionsHistoryAction,
    IFetchCreateJobQuestionsCategoriesAction,
    IPostCompanyQuestionAnswerAction,
    IPostCreateCompanyAction,
    ISendChatMessageAction,
    ISendCreateChatMessageAction,
} from '../redux/interface';
import { userActions, userSelectors } from '../../user/redux';
import {
    createDummyChatHistory,
    createDummyMessage,
    formatCategoriesFromServer,
    formatChatHistoryFromServer,
    formatChatMessagesFromServer,
    formatChatsFromServer,
    formatCompanyQuestionsHistoryFromServer,
} from './manager';
import {
    globalNavigate,
    sendSocketMessage,
    SocketMessages,
} from '../../../base';
import { RoutePaths } from '../../../routes/RoutePaths';
import i18next from 'i18next';

export function* fetchCreateJobCompanysListSaga() {
    try {
        const { response, error } = yield call(fetchCreateJobCompanysList);
        if (response.status === 200) {
            yield put(toolsActions.setCreateJobCompanysList(response.data));
        } else {
            yield call(SnackbarUtils.error, error.data?.error?.message?.[0]);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(toolsActions.fetchCreateJobCompanysListDone());
    }
}

export function* fetchCreateJobQuestionsCategoriesSaga(
    action: PayloadAction<IFetchCreateJobQuestionsCategoriesAction>
) {
    try {
        const { companyId } = action.payload;
        const { response, error } = yield call(
            fetchCreateJobQuestionsCategories,
            companyId
        );
        if (response.status === 200) {
            yield put(
                toolsActions.setCreateJobQuestionsCategories(response.data)
            );
        } else {
            yield call(SnackbarUtils.error, error.data?.error?.message?.[0]);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(toolsActions.fetchCreateJobQuestionsCategoriesDone());
    }
}

export function* postCreateCompanySaga(
    action: PayloadAction<IPostCreateCompanyAction>
) {
    try {
        const { name } = action.payload;
        const { response, error } = yield call(createCompany, { name });
        if (response.status === 201) {
            const myUser: IUser = yield select(userSelectors.getMyUser);
            yield put(
                userActions.setMyUser({
                    myUser: {
                        ...myUser,
                        toolsCompanyProfiles: [
                            ...myUser.toolsCompanyProfiles,
                            response.data,
                        ],
                    },
                })
            );
            yield put(toolsActions.postCreateCompanyDone());
            yield put(toolsActions.setCreateCompanySuccess());
            yield put(toolsActions.setActiveCompany(response.data));
            yield put(
                toolsActions.fetchChatsList({ companyId: response.data.value })
            );
        } else {
            yield call(SnackbarUtils.error, error.data?.error?.message?.[0]);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(toolsActions.postCreateCompanyDone());
    }
}

export function* fetchCompanyCategoriesSaga(
    action: PayloadAction<IFetchCompanyCategoriesAction>
) {
    try {
        const { companyId } = action.payload;
        const { response, error } = yield call(
            fetchCompanyCategories,
            companyId
        );
        if (response.status === 200) {
            yield put(
                toolsActions.setCompanyCategories(
                    formatCategoriesFromServer(response.data)
                )
            );
        } else {
            yield call(SnackbarUtils.error, error.data?.error?.message?.[0]);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(toolsActions.fetchCompanyCategoriesDone());
    }
}

export function* fetchActiveChatQuestionsSaga() {
    try {
        const { response, error } = yield call(fetchActiveChatQuestions);
        if (response.status === 200) {
            yield put(
                toolsActions.setActiveChatQuestions(response.data.results)
            );
        } else {
            yield call(SnackbarUtils.error, error.data?.error?.message?.[0]);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(toolsActions.fetchActiveChatQuestionsDone());
    }
}

export function* fetchChatsListSaga(
    action: PayloadAction<IFetchChatsListAction>
) {
    try {
        const { companyId } = action.payload;
        const { response, error } = yield call(fetchChatsList, companyId);
        if (response.status === 200) {
            yield put(
                toolsActions.setChatsList(
                    formatChatsFromServer(response.data.results)
                )
            );
        } else {
            yield call(SnackbarUtils.error, error.data?.error?.message?.[0]);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(toolsActions.fetchChatsListDone());
    }
}

export function* fetchActiveChatHistorySaga(
    action: PayloadAction<IFetchActiveChatHistoryAction>
) {
    try {
        const { companyId, chatId } = action.payload;
        const { response, error } = yield call(
            fetchChatMessages,
            companyId,
            chatId
        );
        if (response.status === 200) {
            yield put(
                toolsActions.setActiveChatHistory(
                    formatChatHistoryFromServer(response.data)
                )
            );
        } else {
            yield call(SnackbarUtils.error, error.data?.error?.message?.[0]);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(toolsActions.fetchActiveChatHistoryDone());
    }
}

function* modifyMessageInChatHistorySaga(
    message: Array<IServerChatHistoryMessage>,
    index?: number
) {
    const activeChatHistory: IClientChatHistory = yield select(
        toolsSelectors.getActiveChatHistory
    );
    let messageIndex: number;
    if (index !== undefined && index !== null) {
        messageIndex = index;
    } else {
        messageIndex = activeChatHistory.chatHistory.findIndex(
            (chatMessage) =>
                chatMessage.id === TempMessageAndChatIds.TEMP_MESSAGE_ID ||
                chatMessage.id === TempMessageAndChatIds.TEMP_MESSAGE_ID_1
        );
    }
    if (messageIndex !== -1) {
        const newChatHistory: IClientChatHistory = {
            ...activeChatHistory,
            chatHistory: [
                ...activeChatHistory.chatHistory.slice(0, messageIndex),
                ...formatChatMessagesFromServer(message),
                ...activeChatHistory.chatHistory.slice(messageIndex + 1),
            ],
        };
        yield put(toolsActions.setActiveChatHistory(newChatHistory));
    }
}

function* addMessagesToChatHistorySaga(chatHistory: IServerChat) {
    const activeChatHistory: IClientChatHistory = yield select(
        toolsSelectors.getActiveChatHistory
    );
    let updateAwaitingReplyMessage = activeChatHistory.chatHistory.find(
        (message) =>
            message.id === TempMessageAndChatIds.TEMP_AWAITING_REPLY_MESSAGE_ID
    );
    const formattedMessages = formatChatMessagesFromServer(
        chatHistory.messages || []
    );
    if (updateAwaitingReplyMessage) {
        updateAwaitingReplyMessage = formattedMessages[0];
    }
    const activeChatHistoryMessages = activeChatHistory.chatHistory.filter(
        (message) =>
            message.id !== TempMessageAndChatIds.TEMP_AWAITING_REPLY_MESSAGE_ID
    );
    const updatedChatHistory: IClientChatHistory = {
        ...activeChatHistory,
        chatHistory: [
            ...activeChatHistoryMessages,
            ...(updateAwaitingReplyMessage
                ? [updateAwaitingReplyMessage]
                : formattedMessages),
        ],
    };
    yield put(
        toolsActions.setActiveChatHistory({
            ...updatedChatHistory,
            generatedContent: [
                ...activeChatHistory.generatedContent,
                ...formatChatMessagesFromServer(
                    chatHistory.generated_content || []
                ),
            ],
        })
    );
}

export function* sendCreateChatMessageSaga(
    action: PayloadAction<ISendCreateChatMessageAction>
) {
    try {
        const { companyId, answerObject } = action.payload;
        sendSocketMessage({
            title: SocketMessages.TOOL_CHAT_CREATE,
            data: {
                company_profile_id: companyId,
                answer: answerObject.answer,
            },
        });
        const activeChatQuestions: Array<ICompanyQuestion> = yield select(
            toolsSelectors.getActiveChatQuestions
        );
        const myUser: IUser = yield select(userSelectors.getMyUser);
        const dummyHistory: IClientChatHistory = createDummyChatHistory(
            activeChatQuestions,
            answerObject,
            myUser.id || 0
        );
        yield put(toolsActions.setActiveChatHistory(dummyHistory));
        yield put(
            toolsActions.setActiveChatId(TempMessageAndChatIds.TEMP_CHAT_ID)
        );
        yield put(toolsActions.setIsAwaitingJobAssistantReply(true));
    } catch (error) {
        console.log(error);
    }
}

export function* handleCreateChatSuccessMessageSaga(
    action: PayloadAction<IServerChat>
) {
    const { uuid, messages = [] } = action.payload;
    yield put(toolsActions.setActiveChatId(uuid));
    const chatsList: Array<IClientChat> = yield select(
        toolsSelectors.getChatsList
    );
    yield put(
        toolsActions.setChatsList([
            ...formatChatsFromServer([action.payload]),
            ...chatsList,
        ])
    );
    for (const [index, message] of messages.entries()) {
        yield call(modifyMessageInChatHistorySaga, [message], index);
    }
}

export function* handleCreateChatFailedMessageSaga() {
    // yield call(SnackbarUtils.error, error.data?.error?.message?.[0]);
}

export function* postCompanyQuestionAnswerSaga(
    action: PayloadAction<IPostCompanyQuestionAnswerAction>
) {
    try {
        const { companyId, questionId, answer } = action.payload;
        const { response, error } = yield call(
            companyQuestionAnswer,
            companyId,
            questionId,
            answer
        );
        if (response.status === 201) {
            const activeCategory: IClientQuestionCategory = yield select(
                toolsSelectors.getActiveCompanyQuestionsCategory
            );
            const activeCategoryQuestions = activeCategory?.questions;
            const remainingQuestions = activeCategoryQuestions?.filter(
                (question) => String(question.id) !== String(questionId)
            );
            let updatedActiveCategory = {
                ...activeCategory,
                percentage: response.data.percentage,
                questions: remainingQuestions,
            };

            let categoryList: Array<IClientQuestionCategory> = yield select(
                toolsSelectors.getCompanyCategories
            );
            const updatedCategoryList = categoryList.map((category) => {
                if (category.id === activeCategory.id) {
                    return updatedActiveCategory;
                }
                return category;
            });
            yield put(toolsActions.setCompanyCategories(updatedCategoryList));

            categoryList = yield select(toolsSelectors.getCompanyCategories);
            if (remainingQuestions.length === 0) {
                const unfinishedCategory = categoryList.find(
                    (category) => category.percentage < 100
                );
                if (unfinishedCategory) {
                    updatedActiveCategory = {
                        ...unfinishedCategory,
                    };
                }
            }

            yield put(
                toolsActions.setActiveCompanyQuestionsCategory(
                    updatedActiveCategory
                )
            );
        } else if (response.status === 200) {
            const activeEditQuestionAnswer = yield select(
                toolsSelectors.getActiveEditQuestionAnswer
            );
            if (activeEditQuestionAnswer) {
                yield put(toolsActions.setActiveEditQuestionAnswer(null));
                yield put(toolsActions.setActiveCompanyQuestionsCategory(null));
                globalNavigate(RoutePaths.viewAnswerHistory);
            }
        } else {
            yield call(SnackbarUtils.error, error.data?.error?.message?.[0]);
        }
    } catch (error) {
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
        console.log(error);
    } finally {
        yield put(toolsActions.postCompanyQuestionAnswerDone());
    }
}

export function* handleChatReplyFromAISaga(action: PayloadAction<IServerChat>) {
    yield call(addMessagesToChatHistorySaga, action.payload);
    yield put(toolsActions.setIsAwaitingJobAssistantReply(false));
}

export function* sendChatMessageSaga(
    action: PayloadAction<ISendChatMessageAction>
) {
    const { chatId, answerObject } = action.payload;
    sendSocketMessage({
        title: SocketMessages.SEND_CHAT_MESSAGE,
        data: {
            uuid: chatId,
            answer: answerObject.answer,
        },
    });
    const myUser: IUser = yield select(userSelectors.getMyUser);
    const dummyMessage = createDummyMessage(
        answerObject.answer,
        Number(myUser.id) || 0,
        TempMessageAndChatIds.TEMP_MESSAGE_ID,
        MessageTypes.USER
    );
    const dummyAwaitingReplyMessage = createDummyMessage(
        'generating...',
        Number(myUser.id) || 0,
        TempMessageAndChatIds.TEMP_AWAITING_REPLY_MESSAGE_ID,
        MessageTypes.ASSISTANT
    );
    const activeChatHistory: IClientChatHistory = yield select(
        toolsSelectors.getActiveChatHistory
    );
    yield put(
        toolsActions.setActiveChatHistory({
            ...activeChatHistory,
            chatHistory: [
                ...activeChatHistory.chatHistory,
                dummyMessage,
                dummyAwaitingReplyMessage,
            ],
        })
    );
}

export function* handleSendChatMessageSuccessSaga(
    action: PayloadAction<IServerChat>
) {
    yield call(addMessagesToChatHistorySaga, action.payload);
    yield put(toolsActions.setIsAwaitingJobAssistantReply(false));
}

export function* handleSendChatMessageFailedSaga() {
    // yield call(SnackbarUtils.error, error.data?.error?.message?.[0]);
}

export function* fetchCompanyQuestionsHistorySaga(
    action: PayloadAction<IFetchCompanyQuestionsHistoryAction>
) {
    try {
        const { companyId } = action.payload;
        const { response, error } = yield call(
            fetchCompanyQuestionsHistory,
            companyId
        );
        if (response.status === 200) {
            yield put(
                toolsActions.setActiveCompanyQuestionsHistory(
                    formatCompanyQuestionsHistoryFromServer(response.data)
                )
            );
        } else {
            yield call(SnackbarUtils.error, error.data?.error?.message?.[0]);
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(toolsActions.fetchCompanyQuestionsHistoryDone());
    }
}
